import { z } from 'zod';

import { UUID } from 'backend-client-web';

import {
  WithUnknownValuePlaceholder,
  makeUnknownValuePlaceholderSchema,
} from '../unknownValuePlaceholder';
import {
  LoginWithoutHistory,
  Login,
  LoginRevision,
  LOGIN_KEYS,
  LOGIN_REVISION_KEYS,
} from '../vaultContent/login/Login';
import { LOGIN_CREDIT_CARD_KEYS, LoginCreditCard } from '../vaultContent/login/LoginCreditCard';
import { LoginScopeEntryKind } from '../vaultContent/login/LoginScopeEntry';
import { Unprotect } from '../vaultContent/ProtectedValue';

const LOGIN_DIFFERENCE_CREDIT_CARD_IGNORED_KEYS = ['displayMaskedNumber', 'displayType'] as const;

type LoginDifferenceCreditCardIgnoredKey =
  (typeof LOGIN_DIFFERENCE_CREDIT_CARD_IGNORED_KEYS)[number];
type LoginDifferenceCreditCardIncludedKey = Exclude<
  keyof LoginCreditCard,
  LoginDifferenceCreditCardIgnoredKey
>;

const LOGIN_DIFFERENCE_IGNORED_KEYS = [
  'creationProfileId',
  'editProfileId',
  'editTime',
  'creationTime',
  'creationSource',
  'editSource',
  'linkedFrom',
  'linkedTo',
  'linkedViewSecrets',
  'sender',
  'pendingInboxConfirmation',
  'shareLinks',
  'shareSecrets',
  'history',
  'archiveInfo',
  'changeTime',
  'websiteAttributes',
  'baseRevisionId',
  'uiType',
  'leakedPasswordCheck',
  'passwordOrigin',
  'loginSummaryVersion',
  'passwordPolicyCheck',
  'importInfo',
] as const;

type LoginDifferenceIgnoredKey = (typeof LOGIN_DIFFERENCE_IGNORED_KEYS)[number];
type LoginDifferenceIncludedKey<L extends LoginWithoutHistory | Unprotect<LoginWithoutHistory>> =
  string & Exclude<keyof L, LoginDifferenceIgnoredKey | 'creditCard' | 'customFields' | 'scope'>;

function makeLoginDifferenceIncludedKeysSchema<
  L extends LoginWithoutHistory | Unprotect<LoginWithoutHistory>,
>(keys: readonly (string & keyof L)[]) {
  const includedKeys: string[] = keys.filter(
    (k) => !(LOGIN_DIFFERENCE_IGNORED_KEYS as readonly string[]).includes(k),
  );
  return z.string().refine((s): s is LoginDifferenceIncludedKey<L> => includedKeys.includes(s));
}

function makeLoginDifferenceCreditCardIncludedKeysSchema() {
  const includedKeys: string[] = LOGIN_CREDIT_CARD_KEYS.filter(
    (k) => !(LOGIN_DIFFERENCE_CREDIT_CARD_IGNORED_KEYS as readonly string[]).includes(k),
  );
  return z
    .string()
    .refine((s): s is LoginDifferenceCreditCardIncludedKey => includedKeys.includes(s));
}

function makeGenericLoginDifferenceSchema<
  L extends LoginWithoutHistory | Unprotect<LoginWithoutHistory>,
>(keys: readonly (string & keyof L)[]) {
  return makeUnknownValuePlaceholderSchema(
    z.discriminatedUnion('type', [
      z.object({
        type: z.literal('login'),
        key: makeLoginDifferenceIncludedKeysSchema(keys),
      }),
      z.object({
        type: z.literal('creditCard'),
        key: makeLoginDifferenceCreditCardIncludedKeysSchema(),
      }),
      z.object({
        type: z.literal('customField'),
        id: z.uuid(),
        name: z.string(),
      }),
      z.object({
        type: z.literal('scope'),
        kinds: z.array(makeUnknownValuePlaceholderSchema(LoginScopeEntryKind, 'unknown' as const)),
      }),
    ]),
    { type: 'unknown' as const },
  );
}

type GenericLoginDifference<L extends LoginWithoutHistory | Unprotect<LoginWithoutHistory>> =
  WithUnknownValuePlaceholder<
    | { type: 'login'; key: LoginDifferenceIncludedKey<L> }
    | { type: 'creditCard'; key: LoginDifferenceCreditCardIncludedKey }
    | { type: 'customField'; id: UUID; name: string }
    | { type: 'scope'; kinds: WithUnknownValuePlaceholder<LoginScopeEntryKind, 'unknown'>[] },
    { type: 'unknown' }
  >;

type LoginDifference = GenericLoginDifference<Login | Unprotect<Login>>;
const LoginDifference = makeGenericLoginDifferenceSchema(LOGIN_KEYS);

type RevisionDifference = GenericLoginDifference<LoginRevision | Unprotect<LoginRevision>>;
const RevisionDifference = makeGenericLoginDifferenceSchema(LOGIN_REVISION_KEYS);

export {
  LoginDifference,
  RevisionDifference,
  type GenericLoginDifference,
  type LoginDifferenceIncludedKey,
  type LoginDifferenceCreditCardIncludedKey,
  LOGIN_DIFFERENCE_CREDIT_CARD_IGNORED_KEYS,
  LOGIN_DIFFERENCE_IGNORED_KEYS,
};
